import { type TeamAnalysisSummary } from "@/api/tournamentAnalysis";
import { Loader } from "../common/loader";
import { ChevronUp, ChevronDown, Equal } from "lucide-react";
import { getKpiTier, getPointsBadge } from "@/lib/utils";

interface MetricData {
    label: string;
    value: string | number;
    change: number;
    changeType: "increase" | "decrease" | "equal";
    borderColor: string;
}

interface TournamentAnalysisMetricsProps {
    data?: TeamAnalysisSummary;
    isLoading?: boolean;
}

const calculateWinRate = (wins: number, winsOvertime: number, losses: number, lossesOvertime: number): number => {
    const totalGames = wins + winsOvertime + losses + lossesOvertime;
    return totalGames > 0 ? ((wins + winsOvertime) / totalGames) * 100 : 0;
};

const calculateChange = (current: number | undefined, previous: number | null | undefined): number => {
    if (!current || previous === null || previous === undefined) return 0;
    return Math.round((current - previous) * 100) / 100;
};

const calculatePercentageChange = (current: number | undefined, previous: number | null | undefined): number => {
    if (!current || previous === null || previous === undefined || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100 * 100) / 100;
};

const getChangeType = (
    current: number | undefined,
    previous: number | null | undefined
): "increase" | "decrease" | "equal" => {
    if (previous === null || previous === undefined) return "equal";
    const change = calculateChange(current, previous);
    if (Math.abs(change) < 0.01) return "equal";
    return change > 0 ? "increase" : "decrease";
};

export function TournamentAnalysisMetrics({ data, isLoading }: TournamentAnalysisMetricsProps) {
    const currentWinRate = data?.matchSummary
        ? calculateWinRate(
              data.matchSummary.wins,
              data.matchSummary.winsOvertime,
              data.matchSummary.losses,
              data.matchSummary.lossesOvertime
          )
        : 0;

    const previousWinRate = data?.previousMatchSummary
        ? calculateWinRate(
              data.previousMatchSummary.wins,
              data.previousMatchSummary.winsOvertime,
              data.previousMatchSummary.losses,
              data.previousMatchSummary.lossesOvertime
          )
        : null;

    const metrics: MetricData[] = [
        {
            label: "Win Rate",
            value: `${currentWinRate.toFixed(1)}%`,
            change: calculateChange(currentWinRate, previousWinRate),
            changeType: getChangeType(currentWinRate, previousWinRate),
            borderColor: "border-primary",
        },
        {
            label: "Average KPI",
            value: data?.playerKpi?.averageKpi?.toFixed(1) || 0,
            change: calculateChange(data?.playerKpi?.averageKpi, data?.playerKpi?.averageKpiPreviousTournament),
            changeType: getChangeType(data?.playerKpi?.averageKpi, data?.playerKpi?.averageKpiPreviousTournament),
            borderColor: "border-primary",
        },
        {
            label: "Total KPI",
            value: data?.playerKpi?.totalKpi?.toFixed(1) || 0,
            change: calculatePercentageChange(data?.playerKpi?.totalKpi, data?.playerKpi?.totalKpiPreviousTournament),
            changeType: getChangeType(data?.playerKpi?.totalKpi, data?.playerKpi?.totalKpiPreviousTournament),
            borderColor: "border-primary",
        },
    ];

    const renderChangeIndicator = (
        change: number,
        changeType: "increase" | "decrease" | "equal",
        isPercentage: boolean = false
    ) => {
        const configs = {
            equal: {
                icon: Equal,
                colors: "text-on-surface-variant bg-on-surface-variant/10 border-on-surface-variant/20",
                value: "0",
            },
            increase: {
                icon: ChevronUp,
                colors: "text-general-green bg-general-green-background border-general-green/20",
                value: `+${Math.abs(change).toFixed(1)}${isPercentage ? "%" : change < 1 ? "%" : ""}`,
            },
            decrease: {
                icon: ChevronDown,
                colors: "text-general-red bg-general-red-background border-general-red/20",
                value: `-${Math.abs(change).toFixed(1)}${isPercentage ? "%" : change < 1 ? "%" : ""}`,
            },
        };

        const { icon: IconComponent, colors, value } = configs[changeType];

        return (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border ${colors}`}>
                <IconComponent className="w-3 h-3" />
                <span className="text-xs font-semibold">{value}</span>
            </div>
        );
    };

    if (isLoading && !data) {
        return (
            <>
                <div className="flex flex-col gap-2 md:hidden">
                    {metrics.map((metric) => (
                        <div
                            key={metric.label}
                            className={`bg-surface-container-lowest rounded-xl p-3 flex flex-col justify-between border-l-4 ${metric.borderColor}`}
                        >
                            <p className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                {metric.label}
                            </p>
                            <div className="flex items-center justify-center py-4">
                                <Loader message="" className="text-xs" />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="hidden md:grid md:grid-cols-3 gap-4 h-full">
                    {metrics.map((metric, index) => (
                        <div
                            key={metric.label}
                            className={`bg-surface-container-lowest rounded-xl p-6 shadow-sm border-l-4 ${metric.borderColor} flex flex-col`}
                            style={{ animationDelay: `${0.1 + index * 0.1}s` }}
                        >
                            <div className="flex items-center justify-center py-8">
                                <Loader message={`Loading ${metric.label}...`} className="text-sm" />
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    }

    if (!isLoading && !data) {
        return (
            <div className="bg-surface-container-lowest rounded-xl p-4 flex items-center justify-center text-on-surface-variant">
                <p>No tournament data available</p>
            </div>
        );
    }

    const avgKpiTier = data?.playerKpi?.averageKpi != null ? getKpiTier(data.playerKpi.averageKpi) : null;

    const getPreviousValue = (label: string) => {
        if (label === "Average KPI") {
            return data?.playerKpi?.averageKpiPreviousTournament?.toFixed(1) || "N/A";
        }
        if (label === "Total KPI") {
            return data?.playerKpi?.totalKpiPreviousTournament?.toFixed(1) || "N/A";
        }
        if (label === "Win Rate") {
            return previousWinRate !== null ? `${previousWinRate.toFixed(1)}%` : "N/A";
        }
        return "N/A";
    };

    return (
        <>
            <div className="flex flex-col gap-2 md:hidden">
                {metrics.map((metric) => {
                    const prevVal = getPreviousValue(metric.label);
                    const isPercentage = metric.label === "Total KPI";
                    return (
                        <div key={metric.label} className="flex flex-col gap-1">
                            <h3 className="px-4 pt-1 text-sm font-headline font-bold section-title">{metric.label}</h3>
                            <div
                                className={`relative rounded-xl border-l-4 ${metric.borderColor} overflow-hidden grid grid-cols-2`}
                            >
                                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center justify-center gap-1">
                                    <div className="flex flex-col items-center gap-1 bg-surface-container-lowest border border-outline-variant/30 px-2.5 py-1.5 rounded-lg shadow-sm">
                                        <span className="text-[8px] text-on-surface-variant uppercase tracking-widest font-bold">
                                            vs
                                        </span>
                                        {renderChangeIndicator(metric.change, metric.changeType, isPercentage)}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-0.5 px-4 py-3 bg-surface-container-lowest justify-center pr-12">
                                    <span className="text-[9px] text-on-surface-variant uppercase tracking-widest font-bold">
                                        Current
                                    </span>
                                    {metric.label === "Average KPI" && avgKpiTier ? (
                                        (() => {
                                            const { bg, text, border } = getPointsBadge(
                                                parseFloat(String(metric.value))
                                            );
                                            return (
                                                <div
                                                    className={`self-start inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-2xl border ${border}`}
                                                >
                                                    {metric.value}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-2xl font-bold text-on-surface leading-none">
                                            {metric.value}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-0.5 px-4 py-3 bg-surface-container-low justify-center items-end pl-12 min-w-0">
                                    <span className="text-[9px] text-on-surface-variant/60 uppercase tracking-widest font-bold whitespace-nowrap overflow-hidden text-ellipsis max-w-full text-right">
                                        Previous
                                    </span>
                                    {metric.label === "Average KPI" && prevVal !== "N/A" ? (
                                        (() => {
                                            const { bg, text, border } = getPointsBadge(parseFloat(prevVal));
                                            return (
                                                <div
                                                    className={`self-end inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-2xl border ${border}`}
                                                >
                                                    {prevVal}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-2xl font-bold text-on-surface/50 leading-none">
                                            {prevVal}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:grid md:grid-cols-3 gap-4">
                {metrics.map((metric, index) => {
                    const prevVal = getPreviousValue(metric.label);
                    return (
                        <div key={metric.label} className="flex flex-col gap-1">
                            <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">
                                {metric.label}
                            </h3>
                            <div
                                className={`relative rounded-xl shadow-sm border-l-4 ${metric.borderColor} overflow-hidden grid grid-cols-2 flex-1`}
                                style={{ animationDelay: `${0.1 + index * 0.1}s` }}
                            >
                                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center justify-center gap-1">
                                    <div className="flex flex-col items-center gap-1 bg-surface-container-lowest border border-outline-variant/30 px-2 py-1.5 xl:px-3 xl:py-2 rounded-lg shadow-sm">
                                        <span className="text-[9px] text-on-surface-variant uppercase tracking-widest font-bold">
                                            vs
                                        </span>
                                        {renderChangeIndicator(
                                            metric.change,
                                            metric.changeType,
                                            metric.label === "Total KPI"
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 px-3 py-3 xl:px-5 xl:py-4 bg-surface-container-lowest justify-center pr-10 xl:pr-14">
                                    <span className="text-[10px] text-on-surface-variant uppercase tracking-widest font-bold">
                                        Current Season
                                    </span>
                                    {metric.label === "Average KPI" && avgKpiTier ? (
                                        (() => {
                                            const { bg, text, border } = getPointsBadge(
                                                parseFloat(String(metric.value))
                                            );
                                            return (
                                                <div
                                                    className={`self-start inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-xl xl:text-3xl border ${border}`}
                                                >
                                                    {metric.value}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-xl xl:text-3xl font-bold text-on-surface leading-none">
                                            {metric.value}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1 px-3 py-3 xl:px-5 xl:py-4 bg-surface-container-low justify-center items-end pl-10 xl:pl-14 min-w-0">
                                    <span className="text-[10px] text-on-surface-variant/60 uppercase tracking-widest font-bold text-right whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                        {data?.playerKpi.previousTournamentName ?? "Previous Season"}
                                    </span>
                                    {metric.label === "Average KPI" && prevVal !== "N/A" ? (
                                        (() => {
                                            const { bg, text, border } = getPointsBadge(parseFloat(prevVal));
                                            return (
                                                <div
                                                    className={`self-end inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-xl xl:text-3xl border ${border}`}
                                                >
                                                    {prevVal}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-xl xl:text-3xl font-bold text-on-surface/50 leading-none">
                                            {prevVal}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}
